import { logger } from '../utils/logger';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID || '1888393749-t3riksrlkp0fh0oo3lhhhckpk1vg3dd5.apps.googleusercontent.com';

interface GoogleSignInProps {
  onSuccess: (userData: any) => void;
  onError: (error: string) => void;
  disabled?: boolean;
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ 
  onSuccess, 
  onError
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isSignInCompleted, setIsSignInCompleted] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { protocol, hostname, port, pathname, search, hash } = window.location;
      if ((hostname === 'localhost' || hostname === '127.0.0.1') && protocol !== 'http:') {
        const normalizedHost = hostname === '127.0.0.1' ? 'localhost' : hostname;
        const targetPort = port ? `:${port}` : '';
        window.location.replace(`http://${normalizedHost}${targetPort}${pathname}${search}${hash}`);
        return;
      }
      if (hostname === '127.0.0.1' && protocol === 'http:') {
        const targetPort = port ? `:${port}` : '';
        window.location.replace(`http://localhost${targetPort}${pathname}${search}${hash}`);
        return;
      }
    }
  }, []);

  useEffect(() => {
    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsGoogleLoaded(true);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      // Reset sign-in state on unmount
      setIsSignInCompleted(false);
      setIsLoading(false);
    };
  }, []);

  const handleCredentialResponse = useCallback(async (response: any) => {
    if (isSignInCompleted || isLoading) {
      logger.log('Google sign-in already in progress or completed, ignoring duplicate call');
      return;
    }

    setIsLoading(true);
    setIsSignInCompleted(true);
    
    try {
      logger.log('Processing Google sign-in response...');
      // Send the ID token in the body, never the URL, so it stays out of access logs.
      const result = await fetch('/api/v1/auth/google/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential: response.credential }),
      });

      if (!result.ok) {
        let detail = 'Google sign-in failed. Please try again.';
        try {
          const body = await result.json();
          if (typeof body?.detail === 'string') detail = body.detail;
        } catch {
          /* keep the generic message */
        }
        throw new Error(detail);
      }

      const data = await result.json();
      logger.log('Google sign-in successful, calling onSuccess...');
      onSuccess(data);
    } catch (error) {
      logger.error('Google sign-in error:', error);
      setIsSignInCompleted(false);
      onError(error instanceof Error ? error.message : 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [isSignInCompleted, isLoading, onError, onSuccess]);

  // GIS keeps the first callback it was given for the whole page, so route it through
  // a ref: after moving between /login and /register the mounted page handles the result.
  const credentialHandlerRef = useRef(handleCredentialResponse);
  useEffect(() => {
    credentialHandlerRef.current = handleCredentialResponse;
    window.__gotrutaxGoogleCredential = (response: any) => credentialHandlerRef.current(response);
  }, [handleCredentialResponse]);

  useEffect(() => {
    if (!isGoogleLoaded) return;

    // Initialize Google Sign-In only once
    if (window.google && !window.google.accounts.id._initialized) {
      logger.log('Initializing Google Sign-In...');
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: any) => window.__gotrutaxGoogleCredential?.(response),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id._initialized = true;
    }

    // Render the button only if not already rendered
    if (window.google && !document.getElementById('google-signin-button')?.hasChildNodes()) {
      logger.log('Rendering Google Sign-In button...');
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 300,
        }
      );
    }
  }, [isGoogleLoaded]);

  if (!isGoogleLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" ml={2}>
          Loading Google Sign-In...
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Box id="google-signin-button" />
      
      {isLoading && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2">Signing in...</Typography>
        </Box>
      )}
    </Box>
  );
};

// Add Google types to window object
declare global {
  interface Window {
    __gotrutaxGoogleCredential?: (response: any) => void;
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement | null, options: any) => void;
          prompt: () => void;
          _initialized?: boolean;
        };
      };
    };
  }
}

export default GoogleSignIn; 
